/**
 * RunBacktestModal — modal dialog for configuring and running a new backtest.
 *
 * Phase 3: Unified single list. A "strategy" is an aggregation-of-one under the
 * hood. All runs go through the client-side Web Worker via
 * useClientAggregationBacktest (RULE 13 — no server compute).
 *
 * The "strategy" vs "aggregation" distinction is hidden from the user.
 * - Selecting a StrategyConfig wraps it into a 1-sub AggregationBacktestInput.
 * - Selecting an AggregationConfig passes its subs as-is.
 * Both paths call the same aggregate worker.
 */

import { useEffect, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useRunBacktestModalStore } from '../../stores/runBacktestModalStore.js';
import { useStrategyConfigs } from '../../hooks/useConfigurations.js';
import { useAggregations } from '../../hooks/useBacktest.js';
import { useClientAggregationBacktest } from '../../hooks/useClientAggregationBacktest.js';
import { useBacktestStore } from '../../stores/backtestStore.js';
import { useQueryClient } from '@tanstack/react-query';
import { Spinner } from '../Spinner/Spinner.js';
import { VersionBadge } from '../VersionBadge/index.js';
import type { StrategyConfigListItem, AggregationConfig, Timeframe } from '../../types.js';

// ============================================================================
// Default dates
// ============================================================================

function getDefaultDates() {
  const end = new Date();
  const start = new Date();
  start.setMonth(start.getMonth() - 1);
  return {
    startDate: start.toISOString().split('T')[0],
    endDate: end.toISOString().split('T')[0],
  };
}

// ============================================================================
// Shared style constants
// ============================================================================

const inputStyle: React.CSSProperties = {
  width: '100%',
  background: '#2a2a2a',
  border: '1px solid #444',
  borderRadius: 6,
  padding: '7px 10px',
  color: '#e0e0e0',
  fontSize: 13,
  outline: 'none',
  boxSizing: 'border-box',
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: 12,
  color: '#888',
  marginBottom: 4,
};

const sectionStyle: React.CSSProperties = {
  marginBottom: 14,
};

// ============================================================================
// Unified item type for the merged list
// ============================================================================

type RunItem =
  | { kind: 'strategy'; config: StrategyConfigListItem }
  | { kind: 'aggregation'; config: AggregationConfig };

function itemId(item: RunItem): string {
  return item.kind === 'strategy' ? `strategy:${item.config.id}` : `agg:${item.config.id}`;
}

// ============================================================================
// StrategyConfigCard
// ============================================================================

function StrategyConfigCard({
  config,
  selected,
  onClick,
}: {
  config: StrategyConfigListItem;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <div
      onClick={onClick}
      data-testid={`strategy-card-${config.id}`}
      style={{
        padding: '10px 12px',
        background: selected ? 'rgba(33,150,243,0.12)' : '#222',
        border: `1px solid ${selected ? '#2196f3' : '#333'}`,
        borderRadius: 8,
        cursor: 'pointer',
        transition: 'border-color 0.15s, background 0.15s',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 3 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: '#e0e0e0' }}>
            {config.name || config.strategyName}
          </span>
          {config.strategyVersion != null && (
            <VersionBadge
              strategyVersion={config.strategyVersion}
              strategyCurrentVersion={config.strategyCurrentVersion}
              isStale={config.isStale}
            />
          )}
        </div>
        {config.latestRunSharpe != null && (
          <span style={{ fontSize: 11, color: config.latestRunSharpe >= 0 ? '#4caf50' : '#ef5350' }}>
            Sharpe {config.latestRunSharpe.toFixed(2)}
          </span>
        )}
      </div>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 11, color: '#888' }}>{config.strategyName}</span>
        <span style={{ fontSize: 11, color: '#666' }}>&bull;</span>
        <span style={{ fontSize: 11, color: '#888' }}>{config.symbol}</span>
        <span style={{ fontSize: 11, color: '#666' }}>&bull;</span>
        <span style={{ fontSize: 11, color: '#888' }}>{config.timeframe}</span>
      </div>
    </div>
  );
}

// ============================================================================
// AggregationCard
// ============================================================================

function AggregationCard({
  config,
  selected,
  onClick,
}: {
  config: AggregationConfig;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <div
      onClick={onClick}
      data-testid={`aggregation-card-${config.id}`}
      style={{
        padding: '10px 12px',
        background: selected ? 'rgba(33,150,243,0.12)' : '#222',
        border: `1px solid ${selected ? '#2196f3' : '#333'}`,
        borderRadius: 8,
        cursor: 'pointer',
        transition: 'border-color 0.15s, background 0.15s',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 3 }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: '#e0e0e0' }}>{config.name}</span>
        <span style={{ fontSize: 11, color: '#64b5f6', padding: '1px 6px', border: '1px solid #1565c0', borderRadius: 4 }}>
          {config.subStrategies.length} {config.subStrategies.length === 1 ? 'asset' : 'assets'}
        </span>
      </div>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 11, color: '#888' }}>{config.allocationMode.replace(/_/g, ' ')}</span>
        {config.exchange && (
          <>
            <span style={{ fontSize: 11, color: '#666' }}>&bull;</span>
            <span style={{ fontSize: 11, color: '#888' }}>{config.exchange}</span>
          </>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// RunBacktestModal
// ============================================================================

export function RunBacktestModal() {
  const { isOpen, prefillConfig, close } = useRunBacktestModalStore();

  const defaults = getDefaultDates();

  // Search text for filtering config lists
  const [searchText, setSearchText] = useState('');

  // Selected item (strategy config or aggregation)
  const [selectedItemId, setSelectedItemId] = useState<string>('');

  // Runtime fields
  const [exchange, setExchange] = useState('bybit');
  const [startDate, setStartDate] = useState(defaults.startDate);
  const [endDate, setEndDate] = useState(defaults.endDate);
  const [initialCapital, setInitialCapital] = useState(10000);

  const [submitError, setSubmitError] = useState<string | null>(null);

  const { data: strategyConfigs, isLoading: loadingStrategyConfigs } = useStrategyConfigs();
  const { data: aggregations, isLoading: loadingAggregations } = useAggregations();
  const { run: runAggregation, running: isRunning } = useClientAggregationBacktest();
  const { setError } = useBacktestStore();
  const queryClient = useQueryClient();

  // Build the unified, merged list: strategy configs first, then multi-asset agg configs
  // Multi-asset agg configs are those with more than 1 sub-strategy.
  // Single-sub agg configs are not shown (they are the "agg-of-one" wrappers created
  // automatically; users don't interact with them directly).
  const allItems: RunItem[] = [
    ...(strategyConfigs ?? []).map((c): RunItem => ({ kind: 'strategy', config: c })),
    ...(aggregations ?? [])
      .filter((a) => a.subStrategies.length > 1)
      .map((a): RunItem => ({ kind: 'aggregation', config: a })),
  ];

  const filteredItems = allItems.filter((item) => {
    if (!searchText.trim()) return true;
    const q = searchText.toLowerCase();
    if (item.kind === 'strategy') {
      const c = item.config;
      return (
        (c.name ?? '').toLowerCase().includes(q) ||
        c.strategyName.toLowerCase().includes(q) ||
        c.symbol.toLowerCase().includes(q) ||
        c.timeframe.toLowerCase().includes(q)
      );
    } else {
      const a = item.config;
      return (
        a.name.toLowerCase().includes(q) ||
        a.allocationMode.toLowerCase().includes(q) ||
        a.subStrategies.some((s) => s.strategyName.toLowerCase().includes(q))
      );
    }
  });

  const selectedItem = filteredItems.find((item) => itemId(item) === selectedItemId);

  // Apply prefill when modal opens
  useEffect(() => {
    if (isOpen && prefillConfig && strategyConfigs && strategyConfigs.length > 0) {
      const match = strategyConfigs.find(
        (c) =>
          c.strategyName === prefillConfig.strategyName &&
          c.symbol === prefillConfig.symbol &&
          c.timeframe === prefillConfig.timeframe,
      );
      if (match) {
        setSelectedItemId(`strategy:${match.id}`);
      }
    }
    if (isOpen) {
      setSubmitError(null);
      setSearchText('');
    }
  }, [isOpen, prefillConfig, strategyConfigs]);

  // Escape key handler
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    },
    [close],
  );

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, handleKeyDown]);

  const handleSubmit = async () => {
    setSubmitError(null);

    if (!startDate || !endDate) {
      setSubmitError('Start date and end date are required.');
      return;
    }

    if (!selectedItem) {
      setSubmitError('Please select a strategy configuration.');
      return;
    }

    try {
      if (selectedItem.kind === 'strategy') {
        const cfg = selectedItem.config;
        // Wrap single strategy config into an agg-of-one.
        // CP-1: pass strategyConfigId so the server links this MULTI result to
        // the aof1- aggregation record, ensuring it buckets under 'strategies'.
        await runAggregation({
          subStrategies: [
            {
              strategyName: cfg.strategyName,
              symbol: cfg.symbol,
              timeframe: cfg.timeframe as Timeframe,
              params: cfg.params,
              exchange,
            },
          ],
          allocationMode: 'single_strongest',
          maxPositions: 1,
          initialCapital,
          startDate,
          endDate,
          exchange,
          aggregationName: cfg.name || cfg.strategyName,
          strategyConfigId: cfg.id,
        });
      } else {
        const agg = selectedItem.config;
        // Multi-asset aggregation — pass subs as configured
        await runAggregation({
          subStrategies: agg.subStrategies.map((s) => ({
            strategyName: s.strategyName,
            symbol: s.symbol,
            timeframe: s.timeframe as Timeframe,
            params: s.params ?? {},
            exchange: s.exchange ?? exchange,
          })),
          allocationMode: agg.allocationMode as 'single_strongest' | 'weighted_multi' | 'top_n',
          maxPositions: agg.maxPositions,
          initialCapital,
          startDate,
          endDate,
          exchange,
          aggregationName: agg.name,
        });
      }
      queryClient.invalidateQueries({ queryKey: ['explorer-history'] });
      close();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Backtest failed';
      setSubmitError(msg);
      setError(msg);
    }
  };

  if (!isOpen) return null;

  const isLoading = loadingStrategyConfigs || loadingAggregations;
  const canSubmit = !!selectedItem && !!startDate && !!endDate && !isRunning;

  return createPortal(
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {/* Overlay */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background: 'rgba(0,0,0,0.72)',
        }}
        onClick={close}
      />

      {/* Modal content */}
      <div
        style={{
          position: 'relative',
          background: '#1a1a1a',
          border: '1px solid #333',
          borderRadius: 12,
          width: '100%',
          maxWidth: 580,
          maxHeight: '90vh',
          overflowY: 'auto',
          margin: '0 16px',
          boxShadow: '0 24px 64px rgba(0,0,0,0.6)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '18px 20px 14px',
            borderBottom: '1px solid #2a2a2a',
          }}
        >
          <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#e0e0e0' }}>
            New Backtest
          </h2>
          <button
            onClick={close}
            style={{
              background: 'none',
              border: 'none',
              color: '#666',
              cursor: 'pointer',
              padding: 4,
              lineHeight: 1,
              fontSize: 18,
            }}
            aria-label="Close"
          >
            &times;
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: '18px 20px' }}>

          {/* Search input */}
          <div style={sectionStyle}>
            <input
              type="text"
              placeholder="Search by strategy, symbol, timeframe..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ ...inputStyle, marginBottom: 8 }}
            />

            {/* Scrollable config card list */}
            <div
              style={{
                maxHeight: 250,
                overflowY: 'auto',
                display: 'flex',
                flexDirection: 'column',
                gap: 6,
                paddingRight: 2,
              }}
            >
              {isLoading ? (
                <div style={{ padding: '16px 0', textAlign: 'center', color: '#666', fontSize: 13 }}>
                  Loading configurations...
                </div>
              ) : filteredItems.length === 0 ? (
                <div style={{ padding: '16px 0', textAlign: 'center', color: '#666', fontSize: 13 }}>
                  {searchText ? 'No configurations match your search.' : 'No strategy configurations found.'}
                </div>
              ) : (
                filteredItems.map((item) => {
                  const id = itemId(item);
                  const sel = selectedItemId === id;
                  if (item.kind === 'strategy') {
                    return (
                      <StrategyConfigCard
                        key={id}
                        config={item.config}
                        selected={sel}
                        onClick={() => {
                          setSelectedItemId(id);
                          setSubmitError(null);
                        }}
                      />
                    );
                  } else {
                    return (
                      <AggregationCard
                        key={id}
                        config={item.config}
                        selected={sel}
                        onClick={() => {
                          setSelectedItemId(id);
                          setSubmitError(null);
                        }}
                      />
                    );
                  }
                })
              )}
            </div>
          </div>

          {/* Exchange */}
          <div style={sectionStyle}>
            <label style={labelStyle}>Exchange</label>
            <select
              value={exchange}
              onChange={(e) => setExchange(e.target.value)}
              style={inputStyle}
            >
              <option value="bybit">Bybit</option>
              <option value="binance">Binance</option>
            </select>
          </div>

          {/* Date range row */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
            <div>
              <label style={labelStyle}>Start Date</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                style={inputStyle}
              />
            </div>
            <div>
              <label style={labelStyle}>End Date</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                style={inputStyle}
              />
            </div>
          </div>

          {/* Initial Capital */}
          <div style={sectionStyle}>
            <label style={labelStyle}>Initial Capital ($)</label>
            <input
              type="number"
              value={initialCapital}
              onChange={(e) => setInitialCapital(parseFloat(e.target.value) || 0)}
              min={0}
              step={1000}
              style={inputStyle}
            />
          </div>

          {/* Error */}
          {submitError && (
            <div
              style={{
                background: 'rgba(239,68,68,0.12)',
                border: '1px solid rgba(239,68,68,0.4)',
                borderRadius: 8,
                padding: '8px 12px',
                fontSize: 13,
                color: '#f87171',
                marginBottom: 14,
              }}
            >
              {submitError}
            </div>
          )}

          {/* Footer buttons */}
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <button
              onClick={close}
              disabled={isRunning}
              style={{
                padding: '9px 18px',
                background: 'none',
                border: '1px solid #444',
                borderRadius: 8,
                color: '#aaa',
                fontSize: 14,
                cursor: isRunning ? 'not-allowed' : 'pointer',
                opacity: isRunning ? 0.6 : 1,
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={!canSubmit}
              data-testid="run-backtest-submit"
              style={{
                padding: '9px 22px',
                background: canSubmit ? '#2196f3' : '#333',
                border: 'none',
                borderRadius: 8,
                color: canSubmit ? '#fff' : '#666',
                fontSize: 14,
                fontWeight: 600,
                cursor: canSubmit ? 'pointer' : 'not-allowed',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
              }}
            >
              {isRunning ? (
                <>
                  <Spinner size="sm" />
                  Running...
                </>
              ) : (
                'Run Backtest'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
